import {
  Autocomplete,
  createFilterOptions,
  Popper,
  TextField,
} from "@mui/material";
import React from "react";
import { getUniqueOptions } from "../../utils/helpers";

const CustomPopper = (props) => {
  return (
    <Popper
      {...props}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          },
        },
      ]}
      style={{
        ...props.style,
        minWidth: props.anchorEl?.clientWidth, // Set min-width equal to Autocomplete width
        maxWidth: "max-content", // Allow it to grow with content
        textTransform: "capitalize",
      }}
    />
  );
};

const AppAutoComplete = ({
  name,
  options = [],
  value,
  label,
  onChange = () => {},
  onBlur = () => {},
  error,
  touched,
  valueParam,
  labelParam = "label",
  disableClearable,
  disabled,
  placeholder,
  freeSolo = false,
}) => {
  const [searchValue, setSearchValue] = React.useState("");
  const filterOption = createFilterOptions({
    matchFrom: "any",
    trim: true,
    ignoreCase: true,
    stringify: (option) => normalizeSpaces(option[labelParam]),
  });

  // Helper function to normalize spaces
  const normalizeSpaces = (text) =>
    text?.trim().replace(/\s+/g, " ").toLowerCase() || "";

  const customFilterOptions = (options, params) => {
    const { inputValue } = params;
    const normalizedInput = normalizeSpaces(inputValue);

    // First get filtered options based on the normalized input
    const filtered = filterOption(options, {
      ...params,
      inputValue: normalizedInput,
    });

    // Check if any existing option matches exactly or partially
    const exactMatch = options.some(
      (option) => normalizeSpaces(option[labelParam]) === normalizedInput
    );

    const partialMatches = filtered.length > 0;

    // Only add the "Add" option if:
    // 1. No exact match exists (ignoring case and spaces)
    // 2. No partial matches exist
    // 3. The input is not empty
    if (freeSolo && normalizedInput !== "" && !exactMatch && !partialMatches) {
      filtered.push({
        [labelParam]: inputValue.trim(),
        [valueParam]: null,
        isNew: true,
      });
    }

    return filtered;
  };

  const selectOptions = value
    ? options?.find((option) =>
        valueParam ? option?.[valueParam] === value : option === value
      )
    : "";

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleChange = (event, newValue) => {
    if (typeof newValue === "string") {
      // User entered a free text value
      onChange({
        target: {
          name,
          value: newValue.trim(),
          isNew: true,
          rawValue: newValue.trim(),
        },
      });
    } else if (newValue && newValue.isNew) {
      // User selected the "Add new" option
      onChange({
        target: {
          name,
          value: newValue[valueParam],
          isNew: true,
          rawValue: newValue[labelParam],
        },
      });
    } else {
      // User selected an existing option
      onChange({
        target: {
          name,
          value: valueParam ? newValue?.[valueParam] : newValue || "",
          isNew: false,
          rawValue: newValue?.[labelParam] || "",
        },
      });
    }

    setSearchValue("");
  };
  const uniqueOptions = getUniqueOptions(options, labelParam);
  return (
    <Autocomplete
      key={"autocomplete-" + name}
      disabled={disabled}
      name={name}
      options={uniqueOptions || []}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option.trim();
        }
        return option[labelParam] || "";
      }}
      filterOptions={customFilterOptions}
      onChange={handleChange}
      onBlur={onBlur}
      value={selectOptions || null}
      disableClearable={disableClearable}
      PopperComponent={CustomPopper}
      freeSolo={freeSolo}
      renderOption={(props, option) => (
        <li {...props}>
          {option.isNew ? `Add "${option[labelParam]}"` : option[labelParam]}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          error={error && touched}
          helperText={error && touched ? error : null}
          {...params}
          label={label}
          placeholder={placeholder || `Select ${label?.toLowerCase()}`}
          variant="outlined"
          fullWidth
          size="small"
          onChange={handleSearch}
          value={searchValue}
        />
      )}
    />
  );
};

export default AppAutoComplete;
